import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

// Models
import { Account } from "src/app/accounts-v2/models/account.model";
import { Contact } from "src/app/shared/model/contact";
import { Task } from "src/app/shared/model/task";
import { ContactType } from "../../../modules/shared/components/shared-contacts/model/contactType";
import { Gender } from "../../../modules/shared/components/shared-contacts/model/gender";
import { Salutation } from "src/app/contacts/model/salutation.model";
import { Language } from "../../../modules/shared/components/shared-contacts/model/language";
import { ContactRole } from "../../../modules/shared/components/shared-contacts/model/contactRole";
import { RelationshipStatus } from "../../../modules/shared/components/shared-contacts/model/relationshipStatus";
import { MaritalStatus } from "../../../modules/shared/components/shared-contacts/model/maritalStatus";
import { ContactSource } from "../../../modules/shared/components/shared-contacts/model/contactSource";
import { AnnualIncome } from "../../../modules/shared/components/shared-contacts/model/annualIncome";
import { Degree } from "../../../modules/shared/components/shared-contacts/model/degree";
import { FieldOfStudy } from "../../../modules/shared/components/shared-contacts/model/fieldOfStudy";
import { ContactAssociationType } from "../../../modules/shared/components/shared-contacts/model/contactAssociationType";
import { Country } from "../../../modules/shared/components/shared-contacts/model/country";
import { AddressType } from "src/app/accounts-v2/models/fields/addressType.model";
import { AddressClass } from "src/app/accounts-v2/models/fields/addressClass.model";
import { Configuration } from "src/app/accounts-v2/models/configuration/configuration.model";
import { TaskCategory } from "src/app/modules/shared/model/taskCategory";
import { DataWithName } from "../../../modules/shared/model/dataWithName";
import { AccountInventoryDetails } from "src/app/accounts-v2/components/accounts-details/models/accountInventoryDetails.model";

@Injectable({ providedIn: 'root' })
export class StoreService {
    private accountData: BehaviorSubject<Account | Partial<Account>> = new BehaviorSubject<Account | Partial<Account>>(null);
    private activityLogList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    /* Related to Contact  */
    private contactList: BehaviorSubject<Contact[]> = new BehaviorSubject<Contact[]>([]);
    private contactConfigurations: BehaviorSubject<Configuration[]> = new BehaviorSubject<Configuration[]>([]);

    /* Related to Task  */
    private taskList: BehaviorSubject<Task[]> = new BehaviorSubject<Task[]>([]);
    private taskStatuses: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private taskCategories: BehaviorSubject<TaskCategory[]> = new BehaviorSubject<TaskCategory[]>([]);
    private taskModules: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private accountNames: BehaviorSubject<DataWithName[]> = new BehaviorSubject<DataWithName[]>([]);
    private leadNames: BehaviorSubject<DataWithName[]> = new BehaviorSubject<DataWithName[]>([]);
    private opportunityNames: BehaviorSubject<DataWithName[]> = new BehaviorSubject<DataWithName[]>([]);
    private caseNames: BehaviorSubject<DataWithName[]> = new BehaviorSubject<DataWithName[]>([]);
    private reminderTypes: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private taskUsers: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    /* Related to Dropdown Values  */
    private contactTypes: BehaviorSubject<ContactType[]> = new BehaviorSubject<ContactType[]>([]);
    private salutations: BehaviorSubject<Salutation[]> = new BehaviorSubject<Salutation[]>([]);
    private genders: BehaviorSubject<Gender[]> = new BehaviorSubject<Gender[]>([]);
    private languages: BehaviorSubject<Language[]> = new BehaviorSubject<Language[]>([]);
    private contactRoles: BehaviorSubject<ContactRole[]> = new BehaviorSubject<ContactRole[]>([]);
    private relationshipStatuses: BehaviorSubject<RelationshipStatus[]> = new BehaviorSubject<RelationshipStatus[]>([]);
    private maritalStatuses: BehaviorSubject<MaritalStatus[]> = new BehaviorSubject<MaritalStatus[]>([]);
    private contactSources: BehaviorSubject<ContactSource[]> = new BehaviorSubject<ContactSource[]>([]);
    private annualIncomes: BehaviorSubject<AnnualIncome[]> = new BehaviorSubject<AnnualIncome[]>([]);
    private degrees: BehaviorSubject<Degree[]> = new BehaviorSubject<Degree[]>([]);
    private fieldOfStudies: BehaviorSubject<FieldOfStudy[]> = new BehaviorSubject<FieldOfStudy[]>([]);
    private contactAssociationTypes: BehaviorSubject<ContactAssociationType[]> = new BehaviorSubject<ContactAssociationType[]>([]);
    private countries: BehaviorSubject<Country[]> = new BehaviorSubject<Country[]>([]);
    private addressTypes: BehaviorSubject<AddressType[]> = new BehaviorSubject<AddressType[]>([]);
    private addressClasses: BehaviorSubject<AddressClass[]> = new BehaviorSubject<AddressClass[]>([]);

    /* Related to Inventory Details */
    private inventoryDetails: BehaviorSubject<AccountInventoryDetails> = new BehaviorSubject<AccountInventoryDetails>(null);
    private priceTierByAccount: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private priceTierConfiguration: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private allWarehouseList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private groupList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private categoryHierarchyList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    /* Related to Inventory Details Form Dropdown Values */
    private shippingMethods: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private shippingTerms: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private fobPoints: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private paymentMethods: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private paymentTerms: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    // Account
    getAccountData(): BehaviorSubject<Account | Partial<Account>> {
        return this.accountData;
    }

    setAccountData(values: Account | Partial<Account>): void {
        this.accountData.next(values);
    }

    // Activity Logs
    getActivityLogList(): BehaviorSubject<any[]> {
        return this.activityLogList;
    }

    setActivityLogList(values: any[]): void {
        this.activityLogList.next(values);
    }

    // Contacts
    getContactList(): BehaviorSubject<Contact[]> {
        return this.contactList;
    }

    setContactList(values: Contact[]): void {
        this.contactList.next(values);
    }

    // Contact Configurations
    getContactConfigurations(): BehaviorSubject<Configuration[]> {
        return this.contactConfigurations;
    }

    setContactConfigurations(values: Configuration[]): void {
        this.contactConfigurations.next(values);
    }

    // Tasks
    getTaskList(): BehaviorSubject<Task[]> {
        return this.taskList;
    }

    setTaskList(values: Task[]): void {
        this.taskList.next(values);
    }

    // Contact Types
    getContactTypes(): BehaviorSubject<ContactType[]> {
        return this.contactTypes;
    }

    setContactTypes(values: ContactType[]): void {
        this.contactTypes.next(values);
    }

    // Salutations
    getSalutations(): BehaviorSubject<Salutation[]> {
        return this.salutations;
    }

    setSalutations(values: Salutation[]): void {
        this.salutations.next(values);
    }

    // Genders
    getGenders(): BehaviorSubject<Gender[]> {
        return this.genders;
    }

    setGenders(values: Gender[]): void {
        this.genders.next(values);
    }

    // Languages
    getLanguages(): BehaviorSubject<Language[]> {
        return this.languages;
    }

    setLanguages(values: Language[]): void {
        this.languages.next(values);
    }

    // Job Functions
    getContactRoles(): BehaviorSubject<ContactRole[]> {
        return this.contactRoles;
    }

    setContactRoles(values: ContactRole[]): void {
        this.contactRoles.next(values);
    }

    // Relationship Statuses
    getRelationshipStatuses(): BehaviorSubject<RelationshipStatus[]> {
        return this.relationshipStatuses;
    }

    setRelationshipStatuses(values: RelationshipStatus[]): void {
        this.relationshipStatuses.next(values);
    }

    // Marital Statuses
    getMaritalStatuses(): BehaviorSubject<MaritalStatus[]> {
        return this.maritalStatuses;
    }

    setMaritalStatuses(values: MaritalStatus[]): void {
        this.maritalStatuses.next(values);
    }

    // Sources of Contact
    getContactSources(): BehaviorSubject<ContactSource[]> {
        return this.contactSources;
    }

    setContactSources(values: ContactSource[]): void {
        this.contactSources.next(values);
    }

    // Annual Incomes
    getAnnualIncomes(): BehaviorSubject<AnnualIncome[]> {
        return this.annualIncomes;
    }

    setAnnualIncomes(values: AnnualIncome[]): void {
        this.annualIncomes.next(values);
    }

    // Degrees
    getDegrees(): BehaviorSubject<Degree[]> {
        return this.degrees;
    }

    setDegrees(values: Degree[]): void {
        this.degrees.next(values);
    }

    // Field of Studies
    getFieldOfStudies(): BehaviorSubject<FieldOfStudy[]> {
        return this.fieldOfStudies;
    }

    setFieldOfStudies(values: FieldOfStudy[]): void {
        this.fieldOfStudies.next(values);
    }

    // Contact Association Types
    getContactAssociationTypes(): BehaviorSubject<ContactAssociationType[]> {
        return this.contactAssociationTypes;
    }

    setContactAssociationTypes(values: ContactAssociationType[]): void {
        this.contactAssociationTypes.next(values);
    }

    // Countries
    getCountries(): BehaviorSubject<Country[]> {
        return this.countries;
    }

    setCountries(values: Country[]): void {
        this.countries.next(values);
    }

    // Address Types
    getAddressTypes(): BehaviorSubject<AddressType[]> {
        return this.addressTypes;
    }

    setAddressTypes(values: AddressType[]): void {
        this.addressTypes.next(values);
    }

    // Address Classes
    getAddressClasses(): BehaviorSubject<AddressClass[]> {
        return this.addressClasses;
    }

    setAddressClasses(values: AddressClass[]): void {
        this.addressClasses.next(values);
    }

    // Task Statuses
    getTaskStatuses(): BehaviorSubject<any[]> {
        return this.taskStatuses;
    }

    setTaskStatuses(values: any[]): void {
        this.taskStatuses.next(values);
    }

    // Task Categories
    getTaskCategories(): BehaviorSubject<TaskCategory[]> {
        return this.taskCategories;
    }

    setTaskCategories(values: TaskCategory[]): void {
        this.taskCategories.next(values);
    }

    // Task Modules
    getTaskModules(): BehaviorSubject<any[]> {
        return this.taskModules;
    }

    setTaskModules(values: any[]): void {
        this.taskModules.next(values);
    }

    // Account Names
    getAccountNames(): BehaviorSubject<DataWithName[]> {
        return this.accountNames;
    }

    setAccountNames(values: DataWithName[]): void {
        this.accountNames.next(values);
    }

    // Lead Names
    getLeadNames(): BehaviorSubject<DataWithName[]> {
        return this.leadNames;
    }

    setLeadNames(values: DataWithName[]): void {
        this.leadNames.next(values);
    }

    // Opportunity Names
    getOpportunityNames(): BehaviorSubject<DataWithName[]> {
        return this.opportunityNames;
    }

    setOpportunityNames(values: DataWithName[]): void {
        this.opportunityNames.next(values);
    }

    // Case Names
    getCaseNames(): BehaviorSubject<DataWithName[]> {
        return this.caseNames;
    }

    setCaseNames(values: DataWithName[]): void {
        this.caseNames.next(values);
    }

    // Reminder Types
    getReminderTypes(): BehaviorSubject<any[]> {
        return this.reminderTypes;
    }

    setReminderTypes(values: any[]): void {
        this.reminderTypes.next(values);
    }

    // Task Users
    getTaskUsers(): BehaviorSubject<any[]> {
        return this.taskUsers;
    }

    setTaskUsers(values: any[]): void {
        this.taskUsers.next(values);
    }

    // Account Inventory Details
    getInventoryDetails(): BehaviorSubject<AccountInventoryDetails> {
        return this.inventoryDetails;
    }

    setInventoryDetails(value: AccountInventoryDetails): void {
        this.inventoryDetails.next(value);
    }

    // Price Tier by Account and Type
    getPriceTierByAccount(): BehaviorSubject<any> {
        return this.priceTierByAccount;
    }

    setPriceTierByAccount(value: any): void {
        this.priceTierByAccount.next(value);
    }

    // Price Tier Configuration
    getPriceTierConfiguration(): BehaviorSubject<any[]> {
        return this.priceTierConfiguration;
    }

    setPriceTierConfiguration(values: any[]): void {
        this.priceTierConfiguration.next(values);
    }

    // All Ware House Details
    getAllWarehouseList(): BehaviorSubject<any[]> {
        return this.allWarehouseList;
    }

    setAllWarehouseList(values: any[]): void {
        this.allWarehouseList.next(values);
    }

    // Price Tier Configuration
    getGroupList(): BehaviorSubject<any[]> {
        return this.groupList;
    }

    setGroupList(values: any[]): void {
        this.groupList.next(values);
    }

    // Price Tier Configuration
    getCategoryHierarchyList(): BehaviorSubject<any[]> {
        return this.categoryHierarchyList;
    }

    setCategoryHierarchyList(values: any[]): void {
        this.categoryHierarchyList.next(values);
    }

    // Shipping Methods
    getShippingMethods(): BehaviorSubject<any[]> {
        return this.shippingMethods;
    }

    setShippingMethods(values: any[]): void {
        this.shippingMethods.next(values);
    }

    // Shipping Terms
    getShippingTerms(): BehaviorSubject<any[]> {
        return this.shippingTerms;
    }

    setShippingTerms(values: any[]): void {
        this.shippingTerms.next(values);
    }

    // FOB Points
    getFOBPoints(): BehaviorSubject<any[]> {
        return this.fobPoints;
    }

    setFOBPoints(values: any[]): void {
        this.fobPoints.next(values);
    }

    // Payment Methods
    getPaymentMethods(): BehaviorSubject<any[]> {
        return this.paymentMethods;
    }

    setPaymentMethods(values: any[]): void {
        this.paymentMethods.next(values);
    }

    // Payment Terms
    getPaymentTerms(): BehaviorSubject<any[]> {
        return this.paymentTerms;
    }

    setPaymentTerms(values: any[]): void {
        this.paymentTerms.next(values);
    }
}
